import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { Layout } from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { EmptyState } from "@/components/EmptyState";
import { motion } from "framer-motion";
import { ArrowRight, ShoppingBag, Clock } from "lucide-react";

export default function Home() {
  const nav = useNavigate();
  const { t, i18n } = useTranslation();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [recent, setRecent] = useState([]);
  const [loadingRecent, setLoadingRecent] = useState(true);
  const ref = useRef(null);
  const examples = t("home.examples", { returnObjects: true });

  useEffect(() => {
    api
      .get("/decisions")
      .then((r) => setRecent(r.data.slice(0, 4)))
      .catch(() => {})
      .finally(() => setLoadingRecent(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const start = async () => {
    if (query.trim().length < 5) {
      toast.error(t("home.errShort"));
      return;
    }
    setLoading(true);
    try {
      const r = await api.post("/decisions", { query, locale: i18n.resolvedLanguage });
      nav(`/decision/${r.data.id}`);
    } catch (e) {
      toast.error(e.response?.data?.detail || t("home.errStart"));
      setLoading(false);
    }
  };

  return (
    <Layout>
      <div className="mx-auto max-w-3xl px-6 py-16 sm:py-24">
        <motion.div
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
        >
          <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-border bg-card px-3 py-1 text-xs font-medium text-muted-foreground">
            <ShoppingBag className="h-3.5 w-3.5 text-ikb" />
            {t("home.badge")}
          </div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight sm:text-5xl lg:text-6xl">
            {t("home.title")}
          </h1>
          <p className="mt-4 max-w-xl text-base text-muted-foreground">
            {t("home.subtitle")}
          </p>

          <div className="relative mt-8">
            <Textarea
              ref={ref}
              data-testid="decision-query-input"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) start();
              }}
              placeholder={t("home.placeholder")}
              className="min-h-[130px] resize-none rounded-xl border-border bg-card p-5 text-base shadow-sm focus-visible:ring-ikb"
            />
            <Button
              onClick={start}
              disabled={loading}
              className="absolute bottom-4 right-4 bg-primary text-primary-foreground hover:opacity-90"
              data-testid="start-decision-btn"
            >
              {loading ? t("home.framing") : t("home.start")}
              <ArrowRight className="ml-1.5 h-4 w-4" />
            </Button>
          </div>

          <div className="mt-4 flex flex-wrap gap-2">
            {(Array.isArray(examples) ? examples : []).map((ex) => (
              <button
                key={ex}
                onClick={() => setQuery(ex)}
                className="rounded-full border border-border bg-card px-3 py-1.5 text-xs text-muted-foreground transition-colors hover:border-ikb hover:text-foreground"
                data-testid="example-chip"
              >
                {ex}
              </button>
            ))}
          </div>
        </motion.div>

        {!loadingRecent && recent.length === 0 && (
          <div className="mt-16">
            <EmptyState
              variant="decisions"
              testId="home-empty-state"
              title={t("home.emptyTitle")}
              description={t("home.emptyDesc")}
            />
          </div>
        )}

        {recent.length > 0 && (
          <div className="mt-16">
            <div className="mb-4 flex items-center gap-2 text-xs font-semibold uppercase tracking-overline text-muted-foreground">
              <Clock className="h-3.5 w-3.5" /> {t("home.recent")}
            </div>
            <div className="space-y-2">
              {recent.map((d) => (
                <button
                  key={d.id}
                  onClick={() => nav(`/decision/${d.id}`)}
                  className="flex w-full items-center justify-between rounded-lg border border-border bg-card p-4 text-left card-crisp card-interactive"
                  data-testid="recent-decision-item"
                >
                  <div className="min-w-0">
                    <p className="truncate text-sm font-semibold">{d.title}</p>
                    <p className="mt-0.5 text-xs capitalize text-muted-foreground">
                      {d.status} · {d.domain}
                    </p>
                  </div>
                  {d.recommendation && (
                    <span className="ml-4 shrink-0 font-mono text-sm font-bold text-ikb">
                      {d.recommendation.decision_score}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
